import React from 'react';
import { interpretTestData, TEST_EXPLANATIONS, ENERGY_TEST_EXPLANATIONS, displayTestName } from '../../utils/interpretTestData';
import { useTier } from '../../context/TierContext';
import ConvictionRadar from '../charts/ConvictionRadar';
import StockThesis from './StockThesis';
import EarningsSummary from './EarningsSummary';
import ShareGrade from './ShareGrade';
import FundamentalsChart from '../FundamentalsChart';
import DcfVerdict from '../DcfVerdict';
import VerdictChip from '../VerdictChip';
import ConvictionRing from '../ConvictionRing';
import FundHoldings from '../FundHoldings';
import PillarCard from '../PillarCard';

/**
 * PortfolioExpandedRow - Expandable detail panel showing individual test
 * score breakdowns when a row is expanded. Contains quality, setup, convex
 * test columns, macro energy breakdown toggle, and data quality badges.
 */
const TURBULENCE_COLORS = {
  Calm: '#22c55e',
  Choppy: '#eab308',
  Turbulent: '#f97316',
  Crisis: '#ef4444',
};

/**
 * CoherenceBlock — renders within-pillar coherence adjustments (base -> final),
 * identical to the Screener so the portfolio shows the exact same reasoning.
 */
const CoherenceBlock = ({ coherence }) => {
  if (!coherence?.patterns?.length) return null;
  const total = coherence.total ?? 0;
  return (
    <div className="coherence-block">
      <div className="coherence-header">
        <span className="coherence-title">Coherence Adjustments</span>
        <span className={`coherence-total ${total > 0 ? 'positive' : total < 0 ? 'negative' : ''}`}>
          {total > 0 ? `+${total}` : total}
        </span>
      </div>
      <ul className="coherence-patterns">
        {coherence.patterns.map((p) => (
          <li key={p.name} className={`coherence-pattern ${p.magnitude > 0 ? 'positive' : 'negative'}`}>
            <span className="coherence-sign">{p.magnitude > 0 ? `+${p.magnitude}` : p.magnitude}</span>
            <span className="coherence-desc">{p.description}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const PortfolioExpandedRow = ({
  score,
  selectedTest,
  handleTestClick: parentHandleTestClick,
  showEnergyBreakdown,
  toggleEnergyBreakdown,
  getScoreColor,
  symbol,
  turbulence,
  setChartSymbol,
}) => {
  const { hasFeature } = useTier();
  if (!score) return null;

  const convictionScore = score.conviction?.combined_conviction ?? score.combined_conviction;
  const convictionGuidance = score.conviction?.guidance ?? score.guidance;

  // Bind the parent's symbol-aware click handler to this row's symbol so
  // local code can call handleTestClick(testName, testData, e) unchanged.
  const handleTestClick = (testName, testData, e) => {
    parentHandleTestClick(symbol, testName, testData, e);
  };

  // Close-button helper: clear THIS row's selected test only.
  const closeSelectedTest = (e) => {
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    parentHandleTestClick(symbol, selectedTest?.name, null, e);
  };

  // Render test explanation popup with human-readable interpretation
  const renderTestExplanation = (testName, testData, sym = '') => {
    const explanation = TEST_EXPLANATIONS[testName] || ENERGY_TEST_EXPLANATIONS[testName];
    if (!explanation) return null;

    // Get human-readable interpretation of the raw data
    const interpretation = interpretTestData(testName, testData, sym);

    return (
      <div className="test-explanation-popup">
        <div className="test-explanation-header">
          <h6>{explanation.title}</h6>
          <button className="close-btn" onClick={closeSelectedTest}>×</button>
        </div>
        <p className="test-explanation-desc">{explanation.description}</p>
        <div className="test-explanation-scoring">
          <strong>How it's scored:</strong> {explanation.scoring}
        </div>
        {interpretation && (
          <div className="test-explanation-interpretation">
            <strong>This stock's result:</strong>
            <p className="interpretation-text">{interpretation}</p>
          </div>
        )}
        {testData?.notes && (
          <div className="test-explanation-notes">
            <strong>Notes:</strong> {testData.notes}
          </div>
        )}
      </div>
    );
  };

  // Render a section of tests (quality, setup, or convex)
  const renderTestSection = (title, tests, maxScore = 1, pillarScore = null, pillarMax = 100) => {
    if (!tests || Object.keys(tests).length === 0) return null;

    const testEntries = Object.entries(tests).filter(([_, test]) => test);
    if (testEntries.length === 0) return null;

    return (
      <PillarCard name={title.replace(/ Tests$/, '')} score={pillarScore} max={pillarMax} count={testEntries.length}>
        <div className="test-grid">
          {testEntries.map(([testName, test]) => {
            // Safely extract score - check multiple possible field names
            let testScore = 0.5; // Default to neutral
            if (test && typeof test === 'object') {
              if (typeof test.score_0to1 === 'number') {
                testScore = test.score_0to1;
              } else if (typeof test.value === 'number') {
                testScore = test.value;
              } else if (typeof test.score === 'number') {
                testScore = test.score / 100; // Convert 0-100 to 0-1
              }
            }

            // Check if computable - default to true if not specified
            const isComputable = test?.computable !== false && test?.not_applicable !== true;
            const isProxy = test?.proxy === true;
            const isSelected = selectedTest?.name === testName;

            // For non-computable tests, surface the reason inline so the
            // user doesn't have to click to know why a test isn't scoring.
            // Prefer the explicit `notes` field from the backend; otherwise
            // derive a generic message from the raw.reason key.
            let nonComputableReason = '';
            if (!isComputable) {
              if (test?.notes) {
                nonComputableReason = test.notes;
              } else if (test?.raw?.reason) {
                nonComputableReason = `Not computable (${test.raw.reason}).`;
              } else if (test?.not_applicable) {
                nonComputableReason = 'Not applicable for this security type.';
              } else {
                nonComputableReason = 'Data not available for this ticker — does not affect the conviction score.';
              }
            }

            return (
              <div
                key={testName}
                className={`test-item ${!isComputable ? 'non-computable' : ''} ${isSelected ? 'selected' : ''}`}
                onClick={(e) => handleTestClick(testName, test, e)}
                title={isComputable ? 'Click for explanation' : nonComputableReason}
              >
                <div className="test-header">
                  <span className="test-name">
                    {displayTestName(testName)}
                    {isProxy && <span className="proxy-badge" title="Proxy metric">P</span>}
                    <span className="info-icon" title="Click for details">ℹ</span>
                  </span>
                  <span className="test-score">
                    {isComputable ? `${(testScore * 100).toFixed(0)}%` : 'N/A'}
                  </span>
                </div>
                <div className="test-bar">
                  <div
                    className="test-bar-fill"
                    style={{
                      width: isComputable ? `${testScore * 100}%` : '0%',
                      backgroundColor: getScoreColor(testScore * 100, 100)
                    }}
                  />
                </div>
                {!isComputable && (
                  <div className="test-non-computable-note">
                    {nonComputableReason}
                  </div>
                )}
                {isSelected && renderTestExplanation(testName, test, symbol)}
              </div>
            );
          })}
        </div>
      </PillarCard>
    );
  };

  return (
    <div className="score-breakdown">
      <div className="detail-gradehead">
        <ConvictionRing score={convictionScore} />
        <div className="detail-gh-lead">
          <VerdictChip score={convictionScore} conviction big />
          {convictionGuidance && <p className="detail-gh-thesis">{convictionGuidance}</p>}
          <div className="detail-gh-actions">
            {setChartSymbol && (
              <button className="detail-share" onClick={() => setChartSymbol(symbol)}>📈 Price chart</button>
            )}
            <button
              className="detail-share"
              onClick={async () => {
                const url = `${window.location.origin}/grade/${symbol}`;
                try {
                  if (navigator.share) await navigator.share({ title: `${symbol} — AlphaGrade`, url });
                  else await navigator.clipboard.writeText(url);
                } catch { /* cancelled */ }
              }}
            >
              🔗 Share
            </button>
          </div>
        </div>
      </div>
      <DcfVerdict convexTests={score.convex_tests} price={score.current_price} />
      {(score.data_quality?.is_etf || score.data_quality?.is_mutual_fund) && <FundHoldings ticker={symbol} />}
      <p className="weighting-note">
        ⓘ Each test is weighted by how much it matters — so a pillar score isn’t the simple average of its tests.
      </p>
      <div className="breakdown-columns">
        <div className="breakdown-column">
          {renderTestSection('Quality Tests', score.quality_pillars, 1, score.stock_quality_score, 100)}
          <CoherenceBlock coherence={score.quality_coherence} />
        </div>
        <div className="breakdown-column">
          {renderTestSection('Setup Tests', score.setup_pillars, 1, score.stock_setup_score, 100)}
          <CoherenceBlock coherence={score.setup_coherence} />
        </div>
        <div className="breakdown-column">
          {renderTestSection('Convex Tests', score.convex_tests, 1, score.convex_radar_score, 5)}
        </div>
      </div>

      {/* Macro Energy Section */}
      {score.energy_tests && Object.keys(score.energy_tests).length > 0 && (
        <div className="energy-breakdown-section">
          <button
            className="energy-breakdown-toggle"
            onClick={(e) => toggleEnergyBreakdown(symbol, e)}
          >
            <span>📊 Macro Energy Breakdown</span>
            <span className="toggle-icon">{showEnergyBreakdown[symbol] ? '▼' : '▶'}</span>
          </button>
          {showEnergyBreakdown[symbol] && (
            <div className="energy-tests-grid">
              {Object.entries(score.energy_tests).map(([testName, test]) => {
                const testScore = test?.score_0to1 ?? 0.5;
                const isComputable = test?.computable !== false;
                const isSelected = selectedTest?.name === testName;

                return (
                  <div
                    key={testName}
                    className={`energy-test-item ${isSelected ? 'selected' : ''}`}
                    onClick={(e) => handleTestClick(testName, test, e)}
                  >
                    <div className="test-header">
                      <span className="test-name">
                        {testName.replace(/_/g, ' ')}
                        <span className="info-icon">ℹ</span>
                      </span>
                      <span
                        className="test-score"
                        style={{ color: getScoreColor(testScore * 100, 100) }}
                      >
                        {isComputable ? `${(testScore * 100).toFixed(0)}%` : 'N/A'}
                      </span>
                    </div>
                    <div className="test-bar">
                      <div
                        className="test-bar-fill"
                        style={{
                          width: `${testScore * 100}%`,
                          backgroundColor: getScoreColor(testScore * 100, 100)
                        }}
                      />
                    </div>
                    {isSelected && renderTestExplanation(testName, test, 'Market')}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Turbulence Impact */}
      {turbulence && turbulence.turbulence_score > 0 && (
        <div style={{
          margin: '12px 0', padding: '10px 16px', borderRadius: '8px',
          backgroundColor: 'rgba(0,0,0,0.15)',
          border: `1px solid ${TURBULENCE_COLORS[turbulence.turbulence_label] || '#666'}`,
          fontSize: '13px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, color: TURBULENCE_COLORS[turbulence.turbulence_label] }}>
              Turbulence: {turbulence.turbulence_label} ({(turbulence.turbulence_score * 100).toFixed(0)}%)
            </span>
            <span style={{ color: '#ccc' }}>
              Dampener: {(turbulence.dampener * 100).toFixed(0)}%
              {turbulence.dampener < 1.0 && ' — Setup & Convex scores reduced'}
            </span>
            <span style={{ color: '#aaa', fontSize: '12px', marginLeft: 'auto' }}>
              {turbulence.action_guidance}
            </span>
          </div>
          {turbulence.signals && Object.keys(turbulence.signals).length > 0 && (
            <div style={{ display: 'flex', gap: '16px', marginTop: '8px', flexWrap: 'wrap' }}>
              {Object.entries(turbulence.signals).map(([name, sig]) => (
                <div key={name} style={{ fontSize: '11px', color: '#aaa' }}>
                  <span style={{ color: sig.value > 0.5 ? '#f97316' : '#888' }}>
                    {name.replace(/_/g, ' ')}: {(sig.value * 100).toFixed(0)}%
                  </span>
                  <span style={{ color: '#666', marginLeft: '4px' }}>({(sig.weight * 100).toFixed(0)}%w)</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Conviction Radar Chart */}
      <div className="radar-and-actions" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '1rem' }}>
        <ConvictionRadar
          quality={score.stock_quality_score ?? score.quality_score ?? 0}
          setup={score.stock_setup_score ?? score.setup_score ?? 0}
          convex={score.convex_radar_score ?? score.convex_score ?? 0}
          energy={score.energy_matrix_score ?? score.energy_score ?? 0}
          symbol={symbol}
        />
        <div style={{ flex: 1, minWidth: 200, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {hasFeature('ai_thesis') ? (
            <StockThesis symbol={symbol} scoreData={score} />
          ) : (
            <div
              onClick={() => window.location.href = '/pricing'}
              style={{
                padding: '1rem 1.25rem', background: 'linear-gradient(135deg, rgba(100,149,237,0.08), rgba(0,212,170,0.08))',
                border: '1px solid rgba(100,149,237,0.3)', borderRadius: 10, cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#6495ED'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(100,149,237,0.3)'; e.currentTarget.style.transform = 'none'; }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
                <span style={{ fontSize: '1.1rem' }}>🤖</span>
                <span style={{ fontWeight: 700, color: '#6495ED', fontSize: '0.88rem' }}>AI Stock Thesis for {symbol}</span>
              </div>
              <p style={{ margin: 0, fontSize: '0.78rem', color: '#94A3B8', lineHeight: 1.5 }}>
                Get an AI-generated bull case, bear case, key catalysts, and risk analysis for {symbol}. Powered by GPT.
              </p>
              <span style={{ display: 'inline-block', marginTop: '0.5rem', fontSize: '0.72rem', fontWeight: 700, color: '#00D4AA', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Unlock with Premium →
              </span>
            </div>
          )}
          {hasFeature('earnings_summary') ? (
            <EarningsSummary symbol={symbol} />
          ) : (
            <div
              onClick={() => window.location.href = '/pricing'}
              style={{
                padding: '0.75rem 1rem', background: 'rgba(100,149,237,0.06)',
                border: '1px solid rgba(100,149,237,0.2)', borderRadius: 8, cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#6495ED'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(100,149,237,0.2)'; }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <span>📊</span>
                <span style={{ fontWeight: 600, color: '#6495ED', fontSize: '0.82rem' }}>Earnings Summary</span>
                <span style={{ marginLeft: 'auto', fontSize: '0.68rem', color: '#00D4AA', fontWeight: 700 }}>Premium →</span>
              </div>
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.72rem', color: '#64748B' }}>
                AI-generated analysis of {symbol}'s latest earnings report.
              </p>
            </div>
          )}
        </div>
      </div>

      {!(score?.is_crypto || score?.data_quality?.is_crypto) && (
        <FundamentalsChart ticker={symbol} />
      )}

      <ShareGrade
        symbol={symbol}
        conviction={score.conviction?.combined_conviction ?? score.combined_conviction}
        label={score.conviction?.conviction_band ?? score.conviction_band}
      />

      {score.data_quality && (
        <div className="data-quality-info">
          <h5>Data Quality</h5>
          <div className="quality-badges">
            <span className="quality-badge">
              Price: {score.data_quality.price_source || 'unknown'}
            </span>
            <span className="quality-badge">
              History: {score.data_quality.history_points || 0} days
            </span>
            <span className={`quality-badge ${score.data_quality.technicals_available ? 'available' : 'unavailable'}`}>
              Technicals: {score.data_quality.technicals_available ? '✓' : '✗'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortfolioExpandedRow;
